import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { MetricLoggerService } from '@patient-ui/shared-ui/utils';

import { extractApiMetricData, toPascalCase } from './metric-data-extractor';
import { BookingSessionStore } from '../appointment-booking/stores';

export type CtaSourcePage = 'AppointmentDetails' | 'Dashboard' | 'Orders';

export type InsuranceState = 'NoInsurance' | 'Verified' | 'BadVerification';

export type MetricComponentType =
  | 'Alert'
  | 'AppointmentCard'
  | 'AppointmentTable';

export interface MetricLogRequest {
  EventCode:
    | 'SearchAppointment'
    | 'HoldAppointment'
    | 'ScheduleAppointment'
    | 'CTAGetEstimateClicked'
    | 'CTAGetEstimateCompleted'
    | 'CTAInsuranceClicked'
    | 'CTAInsuranceCompleted'
    | 'CTABillPaymentClicked'
    | 'CTABillPaymentCompleted'
    | 'CTACreateAccountCompleted'
    | 'FavoriteLocationAdded';
  ComponentType?: MetricComponentType;
  [key: string]: unknown;
}

export interface ApiMetricData {
  endpoint?: string;
  method?: string;
  statusCode?: number;
  requestData?: unknown;
  responseData?: unknown;
  errorData?: unknown;
  duration?: number;
}

@Injectable({ providedIn: 'root' })
export class AppointmentMetricService {
  constructor(
    private readonly metricLogger: MetricLoggerService,
    private readonly bookingSessionStore: BookingSessionStore
  ) {}

  /**
   * Logs metrics to the POST /metric endpoint
   */
  logMetric(metricData: MetricLogRequest): Observable<boolean> {
    return this.metricLogger.logMetric(metricData);
  }

  /**
   * Logs SearchAppointment event metrics with API data
   */
  logSearchAppointmentEvent(
    apiData: ApiMetricData,
    additionalData: Record<string, unknown> = {}
  ): Observable<boolean> {
    const metricData: MetricLogRequest = {
      EventCode: 'SearchAppointment',
      ...this.extractApiMetrics(apiData),
      ...toPascalCase(additionalData),
    };

    return this.logMetric(metricData);
  }

  /**
   * Logs HoldAppointment event metrics with API data
   */
  logHoldAppointmentEvent(
    apiData: ApiMetricData,
    additionalData: Record<string, unknown> = {}
  ): Observable<boolean> {
    const metricData: MetricLogRequest = {
      EventCode: 'HoldAppointment',
      ...this.extractApiMetrics(apiData),
      ...toPascalCase(additionalData),
    };

    return this.logMetric(metricData);
  }

  /**
   * Logs ScheduleAppointment event metrics with API data
   */
  logScheduleAppointmentEvent(
    apiData: ApiMetricData,
    additionalData: Record<string, unknown> = {}
  ): Observable<boolean> {
    const metricData: MetricLogRequest = {
      EventCode: 'ScheduleAppointment',
      ...this.extractApiMetrics(apiData),
      ...this.extractBookingSessionMetrics(),
      ...toPascalCase(additionalData),
    };

    return this.logMetric(metricData);
  }

  logCtaBillPaymentClicked(
    sourcePage: CtaSourcePage,
    componentType?: MetricComponentType,
    campaignId?: string
  ): Observable<boolean> {
    const metricData: MetricLogRequest = {
      EventCode: 'CTABillPaymentClicked',
      UserFlow: 'RegisteredUser',
      SourcePage: sourcePage,
      RegisteredUser: true,
      UserAgent: navigator.userAgent,
      Url: globalThis.location.pathname,
    };

    if (componentType) {
      metricData.ComponentType = componentType;
    }
    if (campaignId) {
      metricData['CampaignId'] = campaignId;
    }

    return this.logMetric(metricData);
  }

  logCtaInsuranceClicked(
    sourcePage: CtaSourcePage,
    insuranceState: InsuranceState,
    componentType?: MetricComponentType
  ): Observable<boolean> {
    const metricData: MetricLogRequest = {
      EventCode: 'CTAInsuranceClicked',
      UserFlow: 'RegisteredUser',
      SourcePage: sourcePage,
      RegisteredUser: true,
      UserAgent: navigator.userAgent,
      Url: globalThis.location.pathname,
      InsuranceState: insuranceState,
    };

    if (componentType) {
      metricData.ComponentType = componentType;
    }

    return this.logMetric(metricData);
  }

  logCtaGetEstimateClicked(
    sourcePage: CtaSourcePage,
    componentType?: MetricComponentType
  ): Observable<boolean> {
    const metricData: MetricLogRequest = {
      EventCode: 'CTAGetEstimateClicked',
      UserFlow: 'RegisteredUser',
      SourcePage: sourcePage,
      RegisteredUser: true,
      UserAgent: navigator.userAgent,
      Url: globalThis.location.pathname,
    };

    if (componentType) {
      metricData.ComponentType = componentType;
    }

    return this.logMetric(metricData);
  }

  logCtaGetEstimateCompleted(): Observable<boolean> {
    return this.logMetric({
      EventCode: 'CTAGetEstimateCompleted',
      UserFlow: 'RegisteredUser',
      SourcePage: 'Orders',
      RegisteredUser: true,
      UserAgent: navigator.userAgent,
      Url: globalThis.location.pathname,
    });
  }

  logCtaInsuranceCompleted(): Observable<boolean> {
    return this.logMetric({
      EventCode: 'CTAInsuranceCompleted',
      UserFlow: 'RegisteredUser',
      SourcePage: 'AppointmentDetails',
      RegisteredUser: true,
      UserAgent: navigator.userAgent,
      Url: globalThis.location.pathname,
    });
  }

  logCtaBillPaymentCompleted(
    userFlow: 'RegisteredUser' | 'GuestUser',
    campaignId?: string
  ): Observable<boolean> {
    const metricData: MetricLogRequest = {
      EventCode: 'CTABillPaymentCompleted',
      UserFlow: userFlow,
      RegisteredUser: userFlow === 'RegisteredUser',
      UserAgent: navigator.userAgent,
      Url: globalThis.location.pathname,
    };
    if (campaignId) {
      metricData['CampaignId'] = campaignId;
    }
    return this.logMetric(metricData);
  }

  logCtaCreateAccountCompleted(): Observable<boolean> {
    return this.logMetric({
      EventCode: 'CTACreateAccountCompleted',
      UserFlow: 'GuestUser',
      RegisteredUser: false,
      UserAgent: navigator.userAgent,
      Url: globalThis.location.pathname,
    });
  }

  logFavoriteLocationAdded(
    patientId: number,
    locationId: number
  ): Observable<boolean> {
    return this.logMetric({
      EventCode: 'FavoriteLocationAdded',
      PatientId: patientId,
      LocationId: locationId,
      UserFlow: 'RegisteredUser',
      RegisteredUser: true,
      UserAgent: navigator.userAgent,
      Url: globalThis.location.pathname,
    });
  }

  private extractBookingSessionMetrics(): Record<string, unknown> {
    const session = this.bookingSessionStore.getCurrentState();
    return {
      IsFromFavoriteLocation: session.isFromFavoriteLocation,
      IsForLinkedAccount: session.selectedPatient
        ? !session.selectedPatient.isPrimaryAccountHolder
        : false,
      IsConsentFromExistingData:
        session.contactPreferences?.consentFromExistingData ?? true,
    };
  }

  /**
   * Extracts relevant metrics from API requests/responses
   */
  private extractApiMetrics(apiData: ApiMetricData): Record<string, unknown> {
    const metrics = extractApiMetricData(apiData);

    metrics['UserFlow'] = 'RegisteredUser';
    metrics['RegisteredUser'] = true;
    metrics['UserAgent'] = navigator.userAgent;
    metrics['Url'] = globalThis.location.pathname;

    return metrics;
  }
}
